// ============================================================
// THE FIGURE'S STANDING STATES (plan design-gaps, S1; the ruling: "conditions
// are standing states on the blip", each with an onset).
//
// The plate draws a body's conditions with SVG filters at a plate's size
// (components/plate/fx/conditions); at a 12 px ring those would be invisible
// and dear. The map takes the SAME record (engine/plate/plateState: the flags,
// the counted levels, death and Explodes ending the rest) and gives each
// state a SLOT on the figure, where a few strokes can show it: the HEAD (the
// stun's halo, a blind head, the hallucination's double, deafness), the RING
// at its feet (pinned, snared, immobilised, on fire, unaware), the STALK
// (dying's heartbeat, Haywire's glitch, the blood running), or the WHOLE
// figure (fatigue's slump, paralysis, unconsciousness, the mute pulse,
// helplessness). A vehicle's own act on its own parts: the weapon's top face
// flickers red, the crew's faces flash then dim, an explosion throws the head
// off a broken stalk (whole-figure marks), the engine smokes from the head and
// the hull's breach tears the ring. Prone is not a
// state here: the figure's posture already lies it down.
//
// Pure. Zero React dependency.
// ============================================================
import { activeStates, type PlateState } from '../plate/plateState';

/** Where on the figure a state is drawn. */
export type StateSlot = 'head' | 'ring' | 'stalk' | 'whole';

/** Each state the map draws, and its slot. The plate's own order (`activeStates`) is the drawing order. */
export const STATE_SLOTS: Readonly<Record<string, StateSlot>> = {
  dying: 'stalk',
  haywire: 'stalk',
  halluc: 'head',
  paralysed: 'whole',
  unconscious: 'whole',
  helpless: 'whole',
  unaware: 'ring',
  fatigue: 'whole',
  pinned: 'ring',
  stun: 'head',
  fire: 'ring',
  bloodLoss: 'stalk',
  mute: 'whole',
  blind: 'head',
  deaf: 'head',
  snared: 'ring',
  immob: 'ring',
  explodes: 'whole',
  disabled: 'whole',
  // A vehicle's own (S3): its weapon and its crew act on the head's own faces, so they are marks on the figure;
  // the engine's smoke rises from the head, the hull's breach tears the ring.
  wepDest: 'whole',
  engDmg: 'head',
  hullBr: 'ring',
  crewHit: 'whole',
};

export interface MapState {
  /** The plate's key for it (`stun`, `haywire`, `wepDest`...). */
  id: string;
  slot: StateSlot;
  /** 1 for a flag; a counted state's level (Haywire's severity, the hallucination's band, Fatigue, Blood Loss). */
  level: number;
}

/** The standing states a figure wears, in the plate's order; none for a dead one (it lies, and wears nothing). */
export function mapStatesOf(state: PlateState): MapState[] {
  return activeStates(state).flatMap((id) => {
    const slot = STATE_SLOTS[id];
    if (!slot) return [];
    const v = state[id as keyof PlateState];
    return [{ id, slot, level: typeof v === 'number' ? v : 1 }];
  });
}
